/**
 * Custom xyflow edge with a mid-point "+" affordance that inserts a new node
 * between source and target. Shows a subtle bezier curve; switches to a
 * dashed indigo stroke when the edge represents a fan-out (`forEach`).
 */

import { BaseEdge, EdgeLabelRenderer, getBezierPath, type EdgeProps } from '@xyflow/react';
import { PlusIcon } from '@heroicons/react/24/outline';

export interface BindingEdgeData extends Record<string, unknown> {
  forEach?: boolean;
  onInsertBetween: () => void;
}

export function BindingEdge(props: EdgeProps) {
  const { sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, id, style } = props;
  const data = props.data as BindingEdgeData | undefined;
  const [path, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        style={{
          stroke: data?.forEach ? 'rgb(129 140 248)' : 'rgb(148 163 184)',
          strokeWidth: 1.5,
          strokeDasharray: data?.forEach ? '6 4' : undefined,
          ...style
        }}
      />
      <EdgeLabelRenderer>
        <div
          className="nodrag nopan pointer-events-auto absolute"
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`
          }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              data?.onInsertBetween();
            }}
            className="group flex h-6 w-6 items-center justify-center rounded-full border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500 opacity-0 shadow-sm transition hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400 hover:opacity-100 data-[always]:opacity-100"
            title="Insert step here"
          >
            <PlusIcon className="h-3 w-3" />
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
